// src/components/common/Logo.tsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface LogoProps {
  variant?: 'full' | 'icon';
  size?: 'small' | 'medium' | 'large';
  color?: string;
  homePath?: string; // Path to navigate to when logo is clicked
}

const Logo: React.FC<LogoProps> = ({ 
  variant = 'icon',
  size = 'medium',
  homePath = '/home' // Default homepage path
}) => {
  // Size mappings with adjusted dimensions to ensure proper display
  const sizeMap = {
    small: {
      height: 65,
      width: 'auto'
    },
    medium: {
      height: 80,
      width: 'auto'
    },
    large: {
      height: 100,
      width: 'auto'
    }
  };
  
  const { height } = sizeMap[size];
  
  // State for fallback to original logo if lb-logo fails
  const [useFallback, setUseFallback] = useState(false);
  
  const useLarge = size === 'large';
  const pngFile = useFallback
    ? (useLarge ? 'logo512.png' : 'logo192.png')
    : (useLarge ? 'lb-logo512.png' : 'lb-logo192.png');
  const webpFile = useLarge ? 'lb-logo512.webp' : 'lb-logo192.webp';
  const intrinsic = useLarge ? 256 : 192;

  const imgStyle = {
    objectFit: 'contain' as const,
    display: 'block',
    height: `${height}px`,
    width: 'auto',
    maxWidth: 'none',
  };

  return (
    <Link to={homePath} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
      <div className={`logo logo--${size}`} style={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        cursor: 'pointer',
        height: '100%',
        width: 'auto'
      }}>
        {useFallback ? (
          <img
            src={`/${pngFile}`}
            alt="LookBel Logo"
            height={height}
            width={intrinsic}
            style={imgStyle}
            className={variant === 'full' ? 'logo__icon' : ''}
          />
        ) : (
          <picture>
            <source type="image/webp" srcSet={`/${webpFile}`} />
            <img
              src={`/${pngFile}`}
              alt="LookBel Logo"
              height={height}
              width={intrinsic}
              style={imgStyle}
              className={variant === 'full' ? 'logo__icon' : ''}
              onError={() => setUseFallback(true)}
            />
          </picture>
        )}
      </div>
    </Link>
  );
};

export default Logo;